import classNames from "classnames";
import { Immutable } from "immer";
import React, { type JSX } from "react";

import { ConfiguratorTypes } from "../../constants";
import { IProduct } from "../../models/catalogue";
import { DiscountDisplayMode } from "../../models/discountDisplay";
import { FinancingPlans } from "../../models/financing";
import { WebPageURL } from "../../models/nominals";
import { IAPIPrice, IPrice } from "../../models/prices";
import { getTotalPriceWithAddons } from "../../utils/money";
import { useShouldShowPricing } from "../../utils/settings";
import { ConfiguratorFinancing } from "../ConfiguratorFinancing";
import { ConfiguratorProductPrice } from "../ConfiguratorProductPrice";
import { ErrorBoundary } from "../ErrorBoundary";

type IProps = Immutable<{
    price: IAPIPrice;
    financingPlans?: FinancingPlans;
    variant: IProduct;
    selectedAddonPrice: IPrice | null;
    configuratorType?: ConfiguratorTypes;
    financingLink?: WebPageURL | null;
    strikeThroughMSRP: boolean;
    discountDisplayMode: DiscountDisplayMode;
    showFinancingModal?: boolean;
    actualPriceStyle: string;
    overrideFinancingCopy?: string;
    extraClasses?: string;
    supDollarSign?: boolean;
    alternatePriceView?: boolean;
    promoComponents?: JSX.Element | null;
    percentageDiscountFormat?: string;
    absoluteDiscountFormat?: string;
    showStartingAt?: boolean;
    addPriceText?: boolean;
    pricingDisclaimerTooltipText?: string;
}>;

const buildStartingAtText = (
    showStartingAt: boolean | undefined,
    variant: IProduct,
    addPriceText: boolean | undefined,
): React.ReactNode => {
    if (showStartingAt) {
        if (variant.attributes.option_size) {
            return (
                <span className="configurator__price--starting-at">
                    {variant.attributes.option_size.value}
                    {addPriceText && " Price"} starting at
                </span>
            );
        }

        return <span>Starting at</span>;
    }
    return null;
};

export const ConfiguratorPrice = (props: IProps): React.ReactNode => {
    const shouldShowPricing = useShouldShowPricing();
    const totalPriceWithAddons: IPrice = getTotalPriceWithAddons(
        props.price.total,
        props.selectedAddonPrice,
    );

    if (!shouldShowPricing) {
        return null;
    }

    const priceClasses = classNames({
        "configurator__price": true,
        [`configurator__price--${props.configuratorType}`]: true,
        "configurator__price--alternate": props.alternatePriceView,
        "special-financing":
            (!totalPriceWithAddons.per_month ||
                !totalPriceWithAddons.per_month_term_length) &&
            props.showFinancingModal,
    });
    return (
        <div className={priceClasses}>
            {buildStartingAtText(
                props.showStartingAt,
                props.variant,
                props.addPriceText,
            )}
            <ConfiguratorProductPrice
                configuratorType={props.configuratorType}
                strikeThroughMSRP={props.strikeThroughMSRP}
                discountDisplayMode={props.discountDisplayMode}
                percentageDiscountFormat={props.percentageDiscountFormat}
                absoluteDiscountFormat={props.absoluteDiscountFormat}
                actualPriceStyle={props.actualPriceStyle}
                price={totalPriceWithAddons}
                supDollarSign={props.supDollarSign}
                alternatePriceView={props.alternatePriceView}
                pricingDisclaimerTooltipText={
                    props.pricingDisclaimerTooltipText
                }
            />
            <ConfiguratorFinancing
                price={totalPriceWithAddons}
                financingPlans={props.financingPlans}
                variant={props.variant}
                financingLink={props.financingLink}
                configuratorType={props.configuratorType}
                showFinancingModal={props.showFinancingModal}
                overrideFinancingCopy={props.overrideFinancingCopy}
                extraClasses={props.extraClasses}
                alternatePriceView={props.alternatePriceView}
            />
            <ErrorBoundary>{props.promoComponents}</ErrorBoundary>
        </div>
    );
};
